import { ArrowUpRight, ChevronDown, ChevronRight, ChevronUp, Compass, Eye, Info, User } from 'react-feather';
import { Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import styled, { css } from 'styled-components';

import { Locale } from '__generated__/graphql';

import { Event } from '../Dashboard/helpers';
import { EventInfo } from '../../common/Common/EventHeader';
import { FeedbackForm } from '../../common/Feedback/FeedbackForm';
import { GridAlignment, Size, UIProps } from '../../theme/mixins';
import { LinkBlockContainer } from '../../common/UI/Nav';
import { resizedImageUrl } from '../../common/helpers';
import { useAuth } from '../../common/Auth/useAuth';
import { useEditLocaleHandler } from './useEditLocaleHandler';
import { useNavigation } from './useNavigation';
import AtletaLogo, { AtletaLogoElement } from '../../common/Common/AtletaLogo';
import LanguageSelector from '../../common/Common/LanguageSelector';
import OptionsDropdown, { ExternalOption } from '../../common/Common/OptionsDropdown';
import SC from '../../common/UI/SC';
import UI from '../../common/UI';
import useLocale from '../../common/useLocale';
import useMediaDevice from '../../common/useMediaDevice';

interface SectionProps {
  title?: ReactNode;
  header?: ReactNode;
  count?: number;
  secondary?: boolean;
  dashed?: boolean;
  id?: string;
  intro?: ReactNode;
  active?: boolean;
  spacing?: Size;
  sc?: UIProps;
  children: ReactNode;
}

export const Section = ({
  title, header, count, secondary = false, dashed = false, id, intro, active, spacing = 'sm', sc,
  children,
}: SectionProps) => (
  <SectionContainer dashed={dashed} id={id} sc={sc}>
    {(title || intro || header) && (
      <UI.Div>
        {title && <SectionTitle count={count}>{title}</SectionTitle>}
        {intro && <UI.Div sc={{ color: 'gray.500' }}>{intro}</UI.Div>}
        {header}
      </UI.Div>
    )}

    <UI.Card
      sc={{
        hasPanels: true,
        background: secondary ? 'gray.300/20' : undefined,
        spacing,
        active,
        transitionPadding: true,
      }}
    >
      {children}
    </UI.Card>
  </SectionContainer>
);

interface SectionTitleProps {
  children: ReactNode;
  loading?: boolean;
  count?: number;
}

export const SectionTitle = ({ children, loading, count }: SectionTitleProps) => {
  const { formatNumber } = useLocale();

  return (
    <UI.H3 sc={{ fontSize: 2, color: 'gray.600', fontWeight: 500, flex: { alignItems: 'center' } }}>
      <UI.Span sc={{ mr: 1.5 }}>
        {children}
      </UI.Span>
      {loading && (
        <UI.Loader sc={{ brand: 'gray.500' }} />
      )}
      {!loading && typeof count !== 'undefined' && (
        <UI.Small>
          <UI.Badge sc={{ color: 'gray.500' }}>
            {formatNumber(count)}
          </UI.Badge>
        </UI.Small>
      )}
    </UI.H3>
  );
};

interface NavItemProps extends SC<UIProps> {
  to?: string;
  href?: string;
  onClick?: () => void;
  active?: boolean;
  id?: string;
  target?: string;
  rel?: string;
  title?: string;
  icon?: ReactNode;
  alignVertical?: GridAlignment;
  children: ReactNode;
}

export const NavItem = ({
  to, href, onClick, active, id, target, rel, title, icon, alignVertical, sc, children,
}: NavItemProps) => (
  <UI.LinkBlock
    to={to}
    href={href}
    onClick={onClick}
    id={id}
    target={target}
    rel={rel}
    title={title}
    sc={{ pr: [7, 9], relative: true, ...sc }}
  >
    {children}
    <NavItemIcon
      alignVertical={alignVertical}
      sc={{ mt: active ? 4 / 3 : undefined, mr: active ? 4 / 3 : undefined }}
    >
      {typeof icon === 'undefined' ? <ChevronRight /> : icon}
    </NavItemIcon>
  </UI.LinkBlock>
);

export const NavItemIcon = styled(UI.Icon)<{ alignVertical?: GridAlignment; }>`
  ${({ alignVertical = 'top', theme }) => css`
    font-size: 1.25rem;
    color: ${theme.colors.gray[500]};
    transition: all 0.1s ease-in-out;
    position: absolute;
    height: 20px;
    top: ${alignVertical === 'center' ? '50%' : '13px'};
    margin-top: ${alignVertical === 'center' ? '-10px' : 0};
    right: ${theme.gutter * 0.5}px;

    svg {
      stroke-width: 1.5px;
    }

    @media ${theme.devices.md} {
      top: ${alignVertical === 'center' ? '50%' : '16px'};
      right: ${theme.gutter * 0.75}px;
    }
  `}
`;

interface SectionState {
  [key: string]: boolean;
}

interface UseDropdownSectionStateOptions {
  initialState?: SectionState | boolean;
  onOpen?: (key: string) => void;
  onClose?: (key: string) => void;
}

/**
 * Provides state and handlers to control <DropdownSection />.
 */
export const useDropdownSectionState = ({
  initialState = {}, onOpen, onClose,
}: UseDropdownSectionStateOptions = {}) => {
  const defaultKey = 'default';

  const [open, setOpen] = useState<SectionState>(
    typeof initialState === 'boolean' ? { [defaultKey]: initialState } : initialState,
  );

  const isOpen = useCallback((key: string = defaultKey) => !!open[key], [open]);

  const openSection = useCallback((key: string = defaultKey) => {
    setOpen((open) => ({ ...open, [key]: true }));
    onOpen?.(key);
  }, [onOpen]);

  const closeSection = useCallback((key: string = defaultKey) => {
    setOpen((open) => ({ ...open, [key]: false }));
    onClose?.(key);
  }, [onClose]);

  const toggleSection = useCallback((key: string = defaultKey) => {
    const isOpen = !open[key];

    setOpen({ ...open, [key]: isOpen });

    if (isOpen) {
      onOpen?.(key);
    } else {
      onClose?.(key);
    }
  }, [open, onOpen, onClose]);

  return {
    isOpen,
    openSection,
    closeSection,
    toggleSection,
  };
};

const DropdownSectionContent = styled(UI.Div)`
  ${({ theme }) => css`
    background: ${theme.colors.gray[50]};
    border-bottom-left-radius: ${theme.borderRadiuses.xl}px;
    border-bottom-right-radius: ${theme.borderRadiuses.xl}px;

    > * + * {
      border-top: 1px solid ${theme.colors.gray[150]};
    }
  `}
`;

interface DropdownSectionProps {
  preview: ReactNode;
  body: ReactNode;
  title?: string;
  open?: boolean;
  onToggle: Dispatch<SetStateAction<boolean>>;
  id?: string;
  sc?: UIProps;
}

export const DropdownSection = ({
  preview, body, title, open, onToggle, id, sc,
}: DropdownSectionProps) => (
  <Section spacing={open ? 'md' : 'sm'} id={id} active={open}>
    <NavItem
      title={title}
      onClick={body ? () => onToggle((open) => !open) : undefined}
      icon={body ? (!open ? <ChevronDown /> : <ChevronUp />) : null}
      active={open}
      sc={sc}
    >
      {preview}
    </NavItem>
    <UI.Div
      sc={{ padding: 0 }}
      style={{
        opacity: !open ? 0 : undefined,
        transition: 'all 0.15s ease-in-out',
      }}
      id={id ? `${id}_Body` : undefined}
    >
      <UI.AnimateHeight isVisible={open}>
        <DropdownSectionContent>
          {body}
        </DropdownSectionContent>
      </UI.AnimateHeight>
    </UI.Div>
  </Section>
);
interface DropdownItemProps {
  preview: ReactNode;
  body: ReactNode;
  title?: string;
  open?: boolean;
  onToggle: Dispatch<SetStateAction<boolean>>;
  id?: string;
  sc?: UIProps;
}

export const DropdownItem = ({
  preview, body, title, open, onToggle, id, sc,
}: DropdownItemProps) => (
  <>
    <NavItem
      title={title}
      onClick={body ? () => onToggle((open) => !open) : undefined}
      icon={body ? (!open ? <ChevronDown /> : <ChevronUp />) : null}
      active={open}
      sc={sc}
    >
      {preview}
    </NavItem>
    <UI.Div
      sc={{ padding: 0 }}
      style={{
        opacity: !open ? 0 : undefined,
        transition: 'all 0.15s ease-in-out',
      }}
      id={id ? `${id}_Body` : undefined}
    >
      <UI.AnimateHeight isVisible={open}>
        <DropdownSectionContent>
          {body}
        </DropdownSectionContent>
      </UI.AnimateHeight>
    </UI.Div>
  </>
);

interface SectionContainerProps extends SC<UIProps> {
  dashed?: boolean;
}

export const SectionContainer = styled(UI.GridContainer)<SectionContainerProps>`
  ${({ dashed, theme }) => css`
    grid-gap: ${theme.gutter * 0.75}px;

    ${UI.H4} {
      font-weight: 600;
    }

    ${dashed && css`
      > ${UI.Card} > * + * {
        border-top: 2px dashed ${theme.colors.gray[150]};
      }
    `}

    > ${UI.Card} ${LinkBlockContainer}:hover ${NavItemIcon} {
      margin-right: -3px;
      color: ${theme.colors.gray[800]};
    }

    > ${UI.Card} {
      border-radius: ${theme.borderRadiuses.xl}px;

      > :first-child {
        border-top-left-radius: ${theme.borderRadiuses.xl}px;
        border-top-right-radius: ${theme.borderRadiuses.xl}px;
      }

      > :last-child {
        border-bottom-left-radius: ${theme.borderRadiuses.xl}px;
        border-bottom-right-radius: ${theme.borderRadiuses.xl}px;
      }
    }
  `}
`;

export const TicketBadge = styled(UI.Badge)<{ active?: boolean; }>`
  ${({ active, theme }) => css`
    width: 40px;
    min-width: 40px;
    height: 40px;
    border-radius: ${theme.borderRadiuses.md}px;
    background: ${theme.getColor(active ? 'secondary.100' : 'gray.100')};
    background: linear-gradient(-135deg, ${theme.getColor(active ? 'secondary.75' : 'gray.75')} 0%, ${theme.getColor(active ? 'secondary.150' : 'gray.150')} 100%);
    color: ${theme.getColor(active ? 'secondary' : 'gray.400')};
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      stroke-width: 1.5px;
      width: 20px;
      height: 20px;
    }
  `}
`;

interface BreadcrumbsProps {
  hideHome?: boolean;
  children?: ReactNode | ReactNode[];
}

export const Breadcrumbs = ({ hideHome, children }: BreadcrumbsProps) => {
  const { t } = useTranslation();
  const { r } = useNavigation();

  // The children of the Delimit component have to be a flattened array for it to work
  const breadcrumbs = [
    ...!hideHome ? [
      <UI.Link to={r('Portal')} key="home">
        {t('my_account')}
      </UI.Link>,
    ] : [],
    ...Array.isArray(children) ? children : [children],
  ];

  return (
    <UI.Div sc={{ small: true }}>
      <UI.Delimit
        delimiter={<UI.Icon sc={{ muted: true, mx: 1 }}><ChevronRight /></UI.Icon>}
      >
        {breadcrumbs}
      </UI.Delimit>
    </UI.Div>
  );
};

export const Container = styled(UI.Div)<{ size?: 'sm' | 'md' | 'lg'; }>`
  ${({ size = 'sm', theme }) => css`
    min-width: 320px;
    max-width: ${size === 'sm' ? `${theme.breakpoints.md}px` : (size === 'md' ? `${theme.breakpoints.lg}px` : `${theme.breakpoints.xl}px`)};
    width: 100%;
    margin: 0 auto;
    display: grid;

   ${size === 'sm' && css`
      padding: ${theme.gutter * 1.25}px ${theme.gutter * (2 / 3)}px;
      grid-gap: ${theme.gutter * 1.25}px;
    `}

    ${size !== 'sm' && css`
      padding: ${theme.gutter * 1.5}px ${theme.gutter * (2 / 3)}px;
      grid-gap: ${theme.gutter * 1.5}px;
    `}

    @media ${theme.devices.md} {
      ${size === 'sm' && css`
        padding: ${theme.gutter * 1.5}px;
      `}

      ${size !== 'sm' && css`
        padding: ${theme.gutter * 2}px;
      `}
    }

    > ${UI.HR} {
      margin: 0;
    }
  `}
`;

export const Wrapper = styled(UI.FlexContainer)`
  padding-top: 48px;
  flex-direction: column;
  min-height: 100vh;
  align-items: space-between;
`;

interface FillRequiredFieldsProps {
  onClick: () => void;
}

export const FillRequiredFields = ({ onClick }: FillRequiredFieldsProps) => {
  const { t } = useTranslation();

  return (
    <UI.FadeIn>
      <UI.Warning>
        <UI.A
          onClick={onClick}
          sc={{ secondary: true }}
          style={{ fontWeight: 500 }}
          role="button"
        >
          {t('fill_required_fields')}
          {' '}
          <UI.Icon>
            <ArrowUpRight />
          </UI.Icon>
        </UI.A>
      </UI.Warning>
    </UI.FadeIn>
  );
};

export interface NavBarProps {
  onChangeLocale?: (locale: Locale) => unknown;
  showFullLogo?: boolean;
  impersonatedUsername?: string;
  showOrganiserButton?: boolean;
  isHomePage?: boolean;
}

export const NavBar = ({
  onChangeLocale, showFullLogo = false, impersonatedUsername, showOrganiserButton, isHomePage,
}: NavBarProps) => {
  const { t } = useTranslation();
  const { r } = useNavigation();
  const { locale } = useLocale();
  const { user, loading } = useAuth();
  const device = useMediaDevice();

  const [scrolled, setScrolled] = useState(window.pageYOffset > 0);
  const darkMode = !scrolled && isHomePage;

  useEffect(() => {
    const handleScroll = debounce(() => {
      setScrolled(window.pageYOffset > 0);
    });

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const saveLocale = useEditLocaleHandler();

  const handleChangeLocale = (locale: Locale) => {
    saveLocale?.(locale);
    onChangeLocale?.(locale);
  };

  if (loading) {
    return <NavBarContainer />;
  }

  return (
    <NavBarContainer scrolled={scrolled}>
      <Container size="lg" sc={{ py: 0 }}>
        <UI.FlexContainer
          sc={{ justifyContent: 'space-between', alignItems: 'center' }}
          style={{ width: '100%', height: '100%' }}
        >
          <UI.Link
            to={showFullLogo ? r('Home') : r('Portal')}
            sc={{ mb: 0.5 }}
            disabled={!!impersonatedUsername}
            title={t('common:atleta')}
          >
            <AtletaLogo
              symbolOnly={!showFullLogo}
              height={16}
              textColor={darkMode ? 'white' : undefined}
            />
          </UI.Link>
          <UI.FlexContainer sc={{ spaceX: 1 }}>
            {!device.isSmall && showOrganiserButton && (!user || user.project_memberships.length === 0) && (
              <UI.AButton
                href={`https://about.atleta.cc/${locale}`}
                sc={{ blank: true, brand: darkMode ? 'secondary.900' : null, size: 'sm' }}
              >
                <UI.Icon>
                  <Info />
                </UI.Icon>
                {' '}
                {t('website.for_organisers')}
              </UI.AButton>
            )}
            {!device.isSmall && showOrganiserButton && user && user.project_memberships.length > 0 && (
              <OptionsDropdown
                trigger={(
                  <UI.Button sc={{ blank: true, brand: darkMode ? 'secondary.900' : null, size: 'sm' }}>
                    <UI.Icon>
                      <Compass />
                    </UI.Icon>
                    {' '}
                    {t('website.organisation')}
                    {' '}
                    <UI.Icon>
                      <ChevronDown />
                    </UI.Icon>
                  </UI.Button>
                )}
              >
                {user.project_memberships.map((membership) => (
                  <ExternalOption
                    label={membership.project.organisation_name}
                    href={`/b/${membership.project.id}`}
                    key={membership.id}
                  />
                ))}
              </OptionsDropdown>
            )}

            <LanguageSelector
              blank
              brand={darkMode ? 'secondary.900' : undefined}
              hideCurrent
              alignV="bottom"
              alignH="right"
              onChangeLocale={handleChangeLocale}
            />

            <UI.LinkButton
              to={user ? r('Portal') : r('Login')}
              sc={{ size: 'sm', ml: 1, disabled: !!impersonatedUsername, brand: 'primary.600' }}
            >
              {impersonatedUsername && (
                <>
                  <UI.Icon>
                    <Eye />
                  </UI.Icon>
                  {' '}
                  {impersonatedUsername}
                </>
              )}
              {!impersonatedUsername && user && (
                <>
                  <UI.Icon>
                    <User />
                  </UI.Icon>
                  {' '}
                  {user.first_name}
                </>
              )}
              {!user && (
                <>
                  <UI.Icon>
                    <User />
                  </UI.Icon>
                  {' '}
                  {t('log_in')}
                </>
              )}
            </UI.LinkButton>
          </UI.FlexContainer>
        </UI.FlexContainer>
      </Container>
    </NavBarContainer>
  );
};

interface NavBarContainerProps {
  scrolled?: boolean;
}

export const NavBarContainer = styled(UI.Div)<NavBarContainerProps>`
  ${({ theme, scrolled }) => css`
    height: 49px; // Includes border
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 500;
    font-size: ${13 / 16}rem;
    min-height: 60px;
    border-bottom: 1px solid transparent;
    background: transparent;
    transition: all 0.1s ease-in-out;

    ${scrolled && css`
      background: ${theme.transparentize(theme.mix(theme.colors.white, theme.colors.secondary[25], 0.5), 0.25)};
      box-shadow: ${theme.shadows.xs};
      backdrop-filter: blur(10px);
    `}

    > div {
      height: 100%;
    }

    ${AtletaLogoElement} {
      display: block;
    }
 `}
`;

interface EventHeaderProps {
  event: Event;
}

export const EventHeader = ({ event }: EventHeaderProps) => {
  const { locale } = useLocale();

  return (
    <UI.GridContainer sc={{ columns: '60px 1fr', gutter: 0.5, alignVertical: 'center' }}>
      <UI.Div>
        <UI.A href={`${event.checkout_url}?locale=${locale}`}>
          <UI.ProfilePic
            sc={{
              background: event.logo ? 'white' : undefined,
              size: 60,
            }}
          >
            {event.logo && (
              <img
                src={resizedImageUrl(event.logo.url, 300, 'webp')}
                alt={event.title}
              />
            )}
            {!event.logo && (
              <UI.Span><UI.Span>{event.title.charAt(0)}</UI.Span></UI.Span>
            )}
          </UI.ProfilePic>
        </UI.A>
      </UI.Div>
      <UI.Div>
        <UI.H1 sc={{ fontSize: 3, mb: 0.5 }}>
          <UI.A href={`${event.checkout_url}?locale=${locale}`} sc={{ color: 'gray.800' }}>
            {event.title}
          </UI.A>
        </UI.H1>
        <EventInfo event={event} />
      </UI.Div>
    </UI.GridContainer>
  );
};

interface FooterNavProps {
  onChangeLocale?: (locale: Locale) => unknown;
  hideAtletaMission?: boolean;
}

export const FooterNav = ({ onChangeLocale, hideAtletaMission }: FooterNavProps) => {
  const { t } = useTranslation();
  const { r } = useNavigation();
  const { locale } = useLocale();
  const saveLocale = useEditLocaleHandler();

  const handleChangeLocale = (locale: Locale) => {
    saveLocale?.(locale);
    onChangeLocale?.(locale);
  };

  return (
    <UI.Div style={{ marginTop: 'auto' }} role="contentinfo">
      <UI.HR sc={{ my: 0 }} />
      <Container size="lg">
        <UI.GridContainer sc={{ columns: ['1fr', '1fr 1fr'], textAlign: ['center', 'left'] }}>
          <UI.Div sc={{ color: 'gray.500', small: true }}>
            <UI.A href="/" sc={{ inlineBlock: true, mt: [0, 1] }} title={t('common:atleta')}>
              <AtletaLogo height={16} textColor="gray.500" symbolColor="gray.500" />
            </UI.A>
            {!hideAtletaMission && (
              <UI.Paragraph sc={{ mb: 0, mt: 2 }}>
                {t('website.footer_description')}
              </UI.Paragraph>
            )}
          </UI.Div>
          <UI.Div>
            <UI.FlexContainer sc={{ justifyContent: ['center', 'end'] }}>
              <FeedbackForm />
            </UI.FlexContainer>
          </UI.Div>
        </UI.GridContainer>
        <UI.FlexContainer
          sc={{
            justifyContent: [undefined, 'space-between'],
            alignItems: 'center',
            direction: ['column', 'row'],
          }}
        >
          <UI.FlexContainer
            sc={{
              spaceX: [0, 4],
              spaceY: [1, 0],
              justifyContent: ['start', 'start'],
              alignItems: 'center',
              direction: ['column', 'row'],
              small: true,
            }}
          >
            <FooterNavLink to={r('Home')} sc={{ secondary: true, noWrap: true }} exact>
              {t('website.home')}
            </FooterNavLink>
            <FooterNavLink to={r('Calendar')} sc={{ secondary: true, noWrap: true }}>
              {t('website.event_calendar')}
            </FooterNavLink>
            <FooterNavLink to={r('Portal')} sc={{ secondary: true, noWrap: true }}>
              {t('website.account')}
            </FooterNavLink>
            <UI.A
              href={`https://about.atleta.cc/${locale}/participant-support`}
              sc={{ secondary: true, noWrap: true }}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('website.help')}
            </UI.A>
            <UI.A
              href={`https://about.atleta.cc/${locale}`}
              sc={{ secondary: true, noWrap: true }}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('website.atleta_for_organisers')}
            </UI.A>
            <UI.A
              href={`https://about.atleta.cc/${locale}/terms-and-conditions`}
              sc={{ secondary: true, noWrap: true }}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('website.terms')}
            </UI.A>
            <UI.A
              href={`https://about.atleta.cc/${locale}/privacy`}
              sc={{ secondary: true, noWrap: true }}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('website.privacy')}
            </UI.A>
          </UI.FlexContainer>
          <UI.Div sc={{ mt: [3, 0] }}>
            <LanguageSelector blank onChangeLocale={handleChangeLocale} />
          </UI.Div>
        </UI.FlexContainer>
      </Container>
    </UI.Div>
  );
};

const FooterNavLink = styled(UI.NavLink)`
  &.active {
    font-weight: bold;
  }
`;

export default undefined;
